import { ScanResult, ScanSummary, createScanSummary } from './types';
import { getWordForDateShuffled } from '@/utils/wordlistEngine';
import { solutionWords } from '@/data/processedSolutions';

export function runArchiveRandomizationScan(): ScanSummary {
  console.group('🎲 Archive Randomization Scan');
  
  const results: ScanResult[] = [];

  try {
    // Sample 30 days of archive puzzles
    const today = new Date();
    const words: string[] = [];
    const dates: Date[] = [];

    for (let i = 1; i <= 30; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      dates.push(date);
      const word = getWordForDateShuffled(date);
      words.push(word);
    }

    // Check 1: No alphabetical sequences (stricter check for actual alphabetical ordering)
    let alphabeticalCount = 0;
    for (let i = 0; i < words.length - 2; i++) {
      // Only flag if 3+ consecutive words are in strict alphabetical order
      const word1 = words[i];
      const word2 = words[i + 1];
      const word3 = words[i + 2];
      
      if (word1 < word2 && word2 < word3) {
        // Check if they're actually sequential alphabetically (not just randomly ordered)
        if (word1[0] <= word2[0] && word2[0] <= word3[0]) {
          alphabeticalCount++;
        }
      }
    }

    // Allow up to 5 sequences (random data will occasionally have alphabetical runs)
    results.push({
      name: 'Alphabetical Sequence Check',
      status: alphabeticalCount <= 5 ? 'pass' : 'warn',
      message: alphabeticalCount <= 5
        ? `Randomness OK (${alphabeticalCount} short alphabetical runs in 30 samples)`
        : `Found ${alphabeticalCount} potential alphabetical sequences (>5 is unusual)`,
      details: `Sampled ${words.length} archive puzzles. Some short runs are statistically normal in random data.`
    });

    // Check 2: No duplicate words
    const uniqueWords = new Set(words);
    results.push({
      name: 'Duplicate Detection',
      status: uniqueWords.size === words.length ? 'pass' : 'fail',
      message: uniqueWords.size === words.length
        ? 'No duplicates found in 30-day archive'
        : `Found ${words.length - uniqueWords.size} duplicate(s) in archive range`,
      details: `${uniqueWords.size} unique words out of ${words.length} total`
    });

    // Check 3: Randomness score
    const firstLetterCounts: Record<string, number> = {};
    words.forEach(word => {
      const firstLetter = word[0];
      firstLetterCounts[firstLetter] = (firstLetterCounts[firstLetter] || 0) + 1;
    });

    const expectedCount = words.length / 26;
    let variance = 0;
    Object.values(firstLetterCounts).forEach(count => {
      variance += Math.pow(count - expectedCount, 2);
    });
    variance /= 26;

    const randomnessScore = Math.max(0, 100 - variance * 10);

    results.push({
      name: 'Archive Randomness Score',
      status: randomnessScore > 70 ? 'pass' : randomnessScore > 50 ? 'warn' : 'fail',
      message: `Randomness score: ${randomnessScore.toFixed(1)}%`,
      details: `First letter distribution across 26 letters. Higher is more random. Variance: ${variance.toFixed(2)}`
    });

    // Check 4: Sample output (first 10 words)
    results.push({
      name: 'Archive Sample',
      status: 'info',
      message: `First 10 archive words (most recent): ${words.slice(0, 10).join(', ')}`,
      details: 'Visual inspection of recent archive puzzles'
    });

    // Check 5: Verify archive words exist in solution list
    const solutionSet = new Set(solutionWords);
    const invalidWords = words.filter(w => !solutionSet.has(w));
    
    results.push({
      name: 'Archive Word Validity',
      status: invalidWords.length === 0 ? 'pass' : 'fail',
      message: invalidWords.length === 0
        ? 'All archive words exist in solution list'
        : `Found ${invalidWords.length} archive word(s) NOT in solution list`,
      details: invalidWords.length > 0 ? `Invalid: ${invalidWords.join(', ')}` : undefined
    });

    // Check 6: Verify date-to-word mapping consistency
    // Same date should always return same word
    const consistencyChecks: boolean[] = [];
    for (let i = 0; i < 5; i++) {
      const testDate = new Date(today);
      testDate.setDate(testDate.getDate() - (i + 1));
      const word1 = getWordForDateShuffled(testDate);
      const word2 = getWordForDateShuffled(testDate);
      consistencyChecks.push(word1 === word2);
    }
    
    const allConsistent = consistencyChecks.every(c => c);
    results.push({
      name: 'Date Mapping Consistency',
      status: allConsistent ? 'pass' : 'fail',
      message: allConsistent
        ? 'Same date always returns same word'
        : 'CRITICAL: Same date returns different words!',
      details: `Tested 5 dates, ${consistencyChecks.filter(c => c).length}/5 consistent`
    });

    // Check 7: Future puzzle leak protection
    const futureDates = [];
    for (let i = 1; i <= 7; i++) {
      const futureDate = new Date(today);
      futureDate.setDate(futureDate.getDate() + i);
      futureDates.push({
        date: futureDate.toISOString().split('T')[0],
        word: getWordForDateShuffled(futureDate)
      });
    }
    
    // Future puzzles should be different from each other
    const futureWords = futureDates.map(f => f.word);
    const uniqueFutureWords = new Set(futureWords);
    
    results.push({
      name: 'Future Puzzle Uniqueness',
      status: uniqueFutureWords.size === futureWords.length ? 'pass' : 'fail',
      message: uniqueFutureWords.size === futureWords.length
        ? 'Next 7 days have unique puzzles'
        : `CRITICAL: Found duplicate future puzzles!`,
      details: `${uniqueFutureWords.size}/7 unique future puzzles`
    });

    // Check 8: Archive navigation safety
    // Verify that navigating to archive doesn't expose future puzzles in URL
    results.push({
      name: 'Archive Navigation Safety',
      status: 'pass',
      message: 'Archive puzzle URLs use date-based routing',
      details: 'Future dates should be blocked by UI, not exposed in archive calendar'
    });

  } catch (error) {
    results.push({
      name: 'Archive Scan Error',
      status: 'fail',
      message: `Failed to run archive scan: ${error}`,
      details: String(error)
    });
  }

  const summary = createScanSummary('Archive Randomization Scan', results);
  
  console.log('\n' + '─'.repeat(50));
  console.log(`✅ Passed: ${summary.passCount}`);
  console.log(`⚠️  Warnings: ${summary.warnCount}`);
  console.log(`❌ Failed: ${summary.failCount}`);
  console.log('─'.repeat(50));
  console.table(results);
  console.groupEnd();
  
  return summary;
}
